/**
 * My Commission - Salesperson's commission tracking view
 *
 * Shows commission records, payment status, and earnings history.
 * 10% year 1-2, 5% year 3+ for planning; £120 one-time for websites.
 */
import { useState, useEffect, useMemo } from 'react'
import {
  PoundSterling,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Building2,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react'
import { getMyCommission, getMyCommissionSummary } from '@/services/salesApi'
import type { CommissionRecord, CommissionStatus } from '@/types'

const STATUS_CONFIG: Record<CommissionStatus, { bg: string; text: string; label: string; icon: typeof Clock }> = {
  pending_client_payment: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    label: 'Awaiting Client Payment',
    icon: Clock,
  },
  ready_to_pay: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    label: 'Ready to Pay',
    icon: CheckCircle,
  },
  paid: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    label: 'Paid',
    icon: CheckCircle,
  },
  deferred: {
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    label: 'Deferred',
    icon: Calendar,
  },
}

const TIER_LABELS: Record<string, string> = {
  ten_percent: '10%',
  five_percent: '5%',
  flat_website: '£120',
}

interface CommissionSummary {
  periodMonth: number
  periodYear: number
  totalEarned: number
  pendingClientPayment: number
  readyToPay: number
  paid: number
  deferred: number
  recordCount: number
}

export default function MyCommission() {
  const [records, setRecords] = useState<CommissionRecord[]>([])
  const [summary, setSummary] = useState<CommissionSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Current month/year for filtering
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  // Status filter
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | 'all'>('all')

  useEffect(() => {
    fetchCommission()
  }, [selectedMonth, selectedYear])

  const fetchCommission = async () => {
    setLoading(true)
    setError(null)
    try {
      const [recordsData, summaryData] = await Promise.all([
        getMyCommission(selectedMonth, selectedYear),
        getMyCommissionSummary(selectedMonth, selectedYear),
      ])
      setRecords(recordsData)
      setSummary(summaryData)
    } catch {
      setError('Failed to load commission data')
    } finally {
      setLoading(false)
    }
  }

  const filteredRecords = useMemo(() => {
    if (statusFilter === 'all') return records
    return records.filter((r) => r.status === statusFilter)
  }, [records, statusFilter])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const getMonthName = (month: number) => {
    return new Date(2000, month - 1, 1).toLocaleString('en-GB', { month: 'long' })
  }

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const goToNextMonth = () => {
    const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear()
    if (isCurrentMonth) return // Don't go to future months

    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-slate-300 mb-2">{error}</p>
        <button onClick={fetchCommission} className="btn-secondary text-sm">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-500/10">
            <PoundSterling className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">My Commission</h1>
            <p className="text-slate-400">Track your earnings and payment status</p>
          </div>
        </div>
        <button onClick={fetchCommission} className="btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Month Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-lg font-semibold text-white">
              {getMonthName(selectedMonth)} {selectedYear}
            </span>
          </div>
          <button
            onClick={goToNextMonth}
            disabled={isCurrentMonth}
            className={`p-2 rounded-lg transition-colors ${
              isCurrentMonth ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700'
            }`}
          >
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-400">Total Earned</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.totalEarned)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-slate-400">Pending Client</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{formatCurrency(summary.pendingClientPayment)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-slate-400">Ready to Pay</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{formatCurrency(summary.readyToPay)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-400">Paid</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.paid)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-slate-400">Deferred</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{formatCurrency(summary.deferred)}</p>
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-400">Filter:</span>
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            statusFilter === 'all'
              ? 'bg-slate-700 text-white'
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          All ({records.length})
        </button>
        {(Object.keys(STATUS_CONFIG) as CommissionStatus[]).map((status) => {
          const config = STATUS_CONFIG[status]
          const count = records.filter((r) => r.status === status).length
          if (count === 0) return null
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                statusFilter === status
                  ? `${config.bg} ${config.text} border border-current/50`
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {config.label} ({count})
            </button>
          )
        })}
      </div>

      {/* Commission Records */}
      {filteredRecords.length === 0 ? (
        <div className="card text-center py-12">
          <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-200 mb-2">No commission records</h3>
          <p className="text-slate-400">
            {records.length === 0
              ? `No commission records for ${getMonthName(selectedMonth)} ${selectedYear}`
              : 'No records match the selected filter'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecords.map((record) => {
            const statusConfig = STATUS_CONFIG[record.status]
            const StatusIcon = statusConfig.icon

            return (
              <div key={record.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-slate-700/50">
                      <Building2 className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-100">{record.clientName || 'Unknown Client'}</h3>
                      <div className="flex items-center gap-3 text-sm text-slate-400">
                        <span>
                          {record.commissionType === 'planning_recurring' ? 'Planning Service' : 'Website Build'}
                        </span>
                        <span className="text-slate-600">|</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-700/50">
                          {TIER_LABELS[record.rateTier] || record.rateTier}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Deferred info */}
                    {record.status === 'deferred' && record.deferredToMonth && record.deferredToYear && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Deferred to</p>
                        <p className="text-sm text-purple-400">
                          {getMonthName(record.deferredToMonth)} {record.deferredToYear}
                        </p>
                      </div>
                    )}

                    {/* Amount */}
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-400">{formatCurrency(record.amount)}</p>
                    </div>

                    {/* Status */}
                    <div
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${statusConfig.bg}`}
                    >
                      <StatusIcon className={`w-4 h-4 ${statusConfig.text}`} />
                      <span className={`text-sm font-medium ${statusConfig.text}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Paid date */}
                {record.status === 'paid' && record.paidAt && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50 text-xs text-slate-500">
                    Paid on {new Date(record.paidAt).toLocaleDateString('en-GB')}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Commission Rate Info */}
      <div className="card p-4 bg-slate-800/30">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Commission Rate Structure</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5" />
            <div>
              <p className="text-slate-200 font-medium">10% Commission</p>
              <p className="text-slate-500">Planning clients in years 1-2</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-amber-400 mt-1.5" />
            <div>
              <p className="text-slate-200 font-medium">5% Commission</p>
              <p className="text-slate-500">Planning clients in year 3+</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-purple-400 mt-1.5" />
            <div>
              <p className="text-slate-200 font-medium">£120 One-time</p>
              <p className="text-slate-500">Website builds</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
